import axios from 'axios';
import notificationService from './notificationService';
import { isSessionEndCode, sessionEndMessage } from './sessionEnd';

/**
 * Global Axios Interceptor Setup
 * This file sets up interceptors for ALL axios instances, including direct axios calls
 * that don't use the apiClient wrapper.
 */

let isRedirecting = false; // Prevent multiple redirects

// Request Interceptor - Add JWT token and org context to all requests
axios.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add organization context header
    const orgId = localStorage.getItem('orgId');
    if (orgId && !config.headers['X-Org-Id']) {
      config.headers['X-Org-Id'] = orgId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * True when the request was cancelled on purpose (AbortController / react-query superseding a
 * widget's request, navigating to the editor, closing a panel). A cancellation has no response,
 * exactly like a real network failure, so without this check every deliberate abort raised the
 * "Network error" toast — which is what users saw when they clicked Edit while widgets loaded.
 */
export const isRequestCancellation = (error) =>
  axios.isCancel(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError' ||
  error?.config?.signal?.aborted === true;

// Response Interceptor - Handle authentication errors globally
axios.interceptors.response.use(
  (response) => {
    // Successful response, just pass it through
    return response;
  },
  (error) => {
    // A cancelled request is not an error the user should hear about: reject quietly so the
    // caller (which asked for the cancel) can ignore it.
    if (isRequestCancellation(error)) {
      return Promise.reject(error);
    }

    const requestUrl = error.config?.url || '';
    const errorData = error.response?.data || {};

    // Skip notifications for license-related endpoints and errors
    // License errors are handled by LicenseContext and LicenseBlockedPage
    const isLicenseError = errorData.error === 'LICENSE_INVALID';
    const isLicenseEndpoint = requestUrl.includes('/api/license/');

    if (isLicenseError || isLicenseEndpoint) {
      // Let license errors pass through silently - LicenseContext handles them
      return Promise.reject(error);
    }

    // Skip session expiration handling for optional/dashboard endpoints
    // These endpoints might not be available and shouldn't log out the user
    const isOptionalEndpoint = requestUrl.includes('/api/v1.0/alerts') ||
                              requestUrl.includes('/api/v1.0/topology') ||
                              requestUrl.includes('/api/v1.0/kubernetes') ||
                              requestUrl.includes('/api/metadata/applications') ||
                              requestUrl.includes('/api/synthetics') ||
                              requestUrl.includes('/api/network') ||
                              requestUrl.includes('/api/v1.0/infrastructure') ||
                              requestUrl.includes('/api/rum/') ||
                              requestUrl.includes('/api/v1.0/rum/') ||
                              requestUrl.includes('/api/profile') ||
                              requestUrl.includes('/api/infrastructure') ||
                              requestUrl.includes('/dashboard-query-validation/') ||
                              requestUrl.includes('/query-validation/') ||
                              requestUrl.includes('/query/test') ||
                              requestUrl.includes('/sla') ||
                              requestUrl.includes('/metrics/query') ||
                              requestUrl.includes('/metrics/labels');

    // Skip silent failures for requests marked as skipAuthError
    const skipAuthError = error.config?.skipAuthError === true;

    if (isOptionalEndpoint || skipAuthError) {
      // Just reject the error without triggering session expiration
      return Promise.reject(error);
    }

    // Handle authentication errors (401 Unauthorized or 403 Forbidden).
    //
    // Only treat 401 as session-expiration when the server message
    // explicitly says so. Bare 401s from a transient backend issue
    // (org-scoped query 4xx, race during switch-org, etc.) used to nuke
    // the session here — that's how an "Assign tenant" click ended up
    // logging the operator out. Match the {@code apiClient.js} wrapper:
    // require BOTH the 401 status AND a session-y message.
    if ((error.response?.status === 401 || error.response?.status === 403) && !isRedirecting) {
      const errorMessage = errorData.message || '';
      // Match ONLY genuine "your JWT is bad" signals from JwtAuthenticationFilter.
      // Don't match "authentication" or "unauthorized" — Spring Security's
      // default 401 message ("Full authentication is required to access
      // this resource") contains "authentication", and that fires for any
      // background request that races a config reload, hits an unauthed
      // endpoint, or arrives during a cold-start window. Those should NOT
      // log the user out.
      const isSessionExpired = error.response?.status === 401 && (
                              isSessionEndCode(errorData) ||
                              errorMessage.toLowerCase().includes('expired') ||
                              errorMessage.toLowerCase().includes('invalid token'));

      if (isSessionExpired) {
        isRedirecting = true;

        // Clear authentication data
        localStorage.removeItem('authToken');
        localStorage.removeItem('user'); // Clear user data if stored

        // Show session expired message
        notificationService.warning(sessionEndMessage(errorData, 'Your session has expired. Redirecting to login...'), {
          autoHideDuration: 4000,
        });

        // Redirect to root path after a short delay to show the message
        setTimeout(() => {
          isRedirecting = false;
          window.location.href = '/';
        }, 1500);
      } else if (error.response?.status === 403) {
        // This is a permissions issue, not session expiration
        notificationService.error('Access denied. You do not have permission to perform this action.', {
          autoHideDuration: 4000,
        });
      }
    }

    // Handle network errors
    if (!error.response) {
      notificationService.error('Network error. Please check your connection.', {
        autoHideDuration: 3000,
      });
    }

    // Handle datasource unavailable (503 from ClickHouse)
    if (error.response?.status === 503) {
      const msg = error.response?.data?.message || 'Datasource is unavailable';
      notificationService.error(msg, {
        autoHideDuration: 5000,
      });
    }
    // Handle other server errors (500+)
    else if (error.response?.status >= 500) {
      notificationService.error('Server error. Please try again later.', {
        autoHideDuration: 4000,
      });
    }

    return Promise.reject(error);
  }
);

// Export a function to manually trigger session expiration (useful for testing or logout)
export const handleSessionExpired = () => {
  if (!isRedirecting) {
    isRedirecting = true;

    localStorage.removeItem('authToken');
    localStorage.removeItem('user');

    notificationService.warning('Your session has expired. Please login again.', {
      autoHideDuration: 4000,
    });

    setTimeout(() => {
      isRedirecting = false;
      window.location.href = '/';
    }, 1500);
  }
};

// Export a flag to check if currently redirecting
export const isCurrentlyRedirecting = () => isRedirecting;
